<template>
  <div>
    <div
      class="image-upload-main w-100 bg-light-primary cursor-pointer"
      :style="`height: ${height}px`"
    >
      <b-img
        v-if="image && isImage(image)"
        :src="image"
        class="w-100 h-100 uploaded-image"
      />

      <pdf-viewer
        v-else-if="pdfAllowed && image"
        :pdf="image"
        :config="pdfConfig"
        page-scale="page-height"
        class="w-100 h-100 uploaded-image"
      />

      <div
        v-if="pdfAllowed"
        class="h-100"
      >
        <b-row class="d-flex justify-content-center align-items-center w-100 h-100">
          <b-col
            :md="url && !url.includes('base64') ? 2 : 12"
            class="h-100"
          >
            <b-form-file
              :ref="`image-uploader-${id}`"
              v-model="imageFile"
              v-b-tooltip.hover="image ? 'Change CV' : ''"
              class="z-2 icon text-center h-100"
            >
              <template #placeholder>
                <feather-icon
                  icon="UploadIcon"
                  size="32"
                />
                <p
                  v-if="!url"
                  class="mb-0 small text-dark"
                >
                  {{ $t('messages.upload-file-size') }}
                </p>
              </template>
            </b-form-file>
          </b-col>
          <b-col
            v-if="url && !url.includes('base64')"
            v-b-tooltip.hover="'Download CV'"
            :md="2"
            class="icon cursor-pointer z-2"
            @click="downloadCV"
          >
            <feather-icon
              icon="DownloadIcon"
              size="32"
            />
          </b-col>
        </b-row>
      </div>
      <input
        v-else
        :ref="`image-uploader-${id}`"
        type="file"
        style="height:0px;width:0px;opacity:0px"
        @input="imageSelected"
      >
      <div
        v-if="!pdfAllowed"
        class="z-2"
      >
        <b-button
          ref="imageUploadButton"
          variant="primary"
          @click="$refs[`image-uploader-${id}`].click()"
        >
          <feather-icon
            icon="UploadIcon"
            class="mr-50"
          />
          <span
            v-if="!smallButton"
            class="align-middle"
          >{{ image ? `Change ${title}` :`Upload ${title}` }}</span>
        </b-button>
      </div>

      <div
        v-show="image || alwaysShowDelete"
        class="delete-button z-2"
      >
        <b-button
          v-b-tooltip.hover
          variant="danger"
          size="sm"
          :title="`Remove ${title}`"
          @click="removeImage"
        >
          <feather-icon
            icon="XIcon"
          />
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton, BImg, VBTooltip, BFormFile, BRow, BCol,
} from 'bootstrap-vue'
import PdfViewer from 'vue-pdf-app'
import 'vue-pdf-app/dist/icons/main.css'

export default {
  components: {
    BButton, BImg, BFormFile, BRow, BCol, PdfViewer,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    height: {
      type: [Number, String],
      default: 300,
    },
    title: {
      type: String,
      default: 'Image',
    },
    url: {
      type: String,
      default: null,
    },
    smallButton: {
      type: Boolean,
      default: false,
    },
    alwaysShowDelete: {
      type: Boolean,
      default: false,
    },
    pdfAllowed: {
      type: Boolean,
      default: false,
    },
    pdfModalTitle: {
      type: String,
      default: 'Document Preview',
    },
  },
  data() {
    return {
      id: Math.random().toString(36).substr(2, 5),
      isUploadingImage: false,
      imageFile: null,
      image: this.url,
      previousImage: null,
      pdfConfig: {
        toolbar: false,
      },
      pdfModalConfig: {
        toolbar: {
          toolbarViewerRight: {
            openFile: false,
          },
        },
      },
    }
  },
  watch: {
    url(url) {
      if (url === 'https://via.placeholder.com/350x150') {
        this.$refs[`image-uploader-${this.id}`].value = null
        this.image = null
      }
      this.image = url
    },
    imageFile() {
      const reader = new FileReader()
      reader.addEventListener(
        'load',
        () => {
          if (this.imageFile.size > 1024 * 1024 * 5) {
            this.showError(`Please select ${this.pdfAllowed ? 'document' : 'images'} of less than 5MB`)
            this.$refs[`image-uploader-${this.id}`].value = null
            return
          }
          this.previousImage = this.image
          this.image = reader.result
          this.$emit('imageUploaded', this.imageFile, this.image)
        },
        false,
      )
      if (this.imageFile) {
        if (this.pdfAllowed && (/\.(pdf|jpe?g|png)$/i.test(this.imageFile.name))) {
          reader.readAsDataURL(this.imageFile)
        } else if (!this.pdfAllowed && (/\.(jpe?g|png|gif)$/i.test(this.imageFile.name))) {
          reader.readAsDataURL(this.imageFile)
        } else if (this.pdfAllowed) {
          this.showError('The accepted document formats are JPG, PNG or PDF')
          this.resetImage()
        } else {
          this.showError('The accepted image formats are JPG, GIF or PNG')
          this.resetImage()
        }
      }
    },
  },

  methods: {
    resetImage() {
      this.imageFile = null
      this.image = null
      this.$refs[`image-uploader-${this.id}`].reset()
    },
    removeImage() {
      this.imageFile = null
      this.image = null
      this.previousImage = null
      if (this.$refs[`image-uploader-${this.id}`]) this.$refs[`image-uploader-${this.id}`].value = null
      this.$emit('imageRemoved')
    },
    imageSelected(e) {
      if (!e.target.files[0]) return
      // eslint-disable-next-line prefer-destructuring
      this.imageFile = e.target.files[0]
    },
    isImage(document) {
      if (!document) return false

      let documentType = ''
      if (document && document.startsWith('http')) {
        const doc = document.split('/')
        documentType = doc[doc.length - 1]?.split('.')[1]
      } else {
        const doc = document.split(';')[0]?.split('/')
        documentType = doc[1]
      }
      return documentType !== 'pdf'
    },
    downloadCV() {
      if (this.image) {
        const link = document.createElement('a')
        link.target = '_blank'
        link.href = this.image
        link.download = this.image.split('/').pop()
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.image-upload-main{
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    width: 100%;
    overflow: hidden;
}
.uploaded-image{
    position: absolute;
    object-fit: cover;
    z-index: 1;
    opacity: 0.5;
    border-radius: 8px;
}
  .z-2{
    z-index: 2;
  }
.delete-button{
  position: absolute;
  left: 8px;
  right: 8px;
  top: 8px;
}
.file-icon {
  padding-bottom: 70px;
}
.upload-icon {
  top: 58px;
}
.icon {
  color: #d6d6d6;
  transition: color 0.3s;
}
.icon:hover {
  color: #f1f1f1
}
</style>

<style lang="scss">
.image-upload-main {
  .b-form-file {
    height: 100%;
    width: 100%;
  }
  .custom-file-label {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: transparent !important;
    transition: color 0.3s;
    border: none;
    color: #d6d6d6;
    cursor: pointer;
  }
  .custom-file-label:hover {
    color: #f1f1f1
  }
  .custom-file-label:after {
    display: none;
  }
  #thumbnailView {
    width: 100% !important;
  }
}
</style>
